import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Campaign, CampaignFilters, CampaignStats, CreateCampaignInput } from './types'
import type { Lead } from '@/features/leads/types'
import { useApi } from '@/hooks/use-api'
import type { CampaignBrief } from '@dealcloser/shared'

// Query keys
export const campaignKeys = {
  all: ['campaigns'] as const,
  lists: () => [...campaignKeys.all, 'list'] as const,
  list: (filters: CampaignFilters) => [...campaignKeys.lists(), filters] as const,
  details: () => [...campaignKeys.all, 'detail'] as const,
  detail: (id: string) => [...campaignKeys.details(), id] as const,
  stats: (id: string) => [...campaignKeys.all, 'stats', id] as const,
}

// Hooks

export function useCampaigns(filters: CampaignFilters = {}) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: campaignKeys.list(filters),
    queryFn: async (): Promise<readonly Campaign[]> => {
      const params = new URLSearchParams()
      if (filters.status && filters.status !== 'all') {
        params.set('status', filters.status)
      }
      const query = params.toString() ? `?${params.toString()}` : ''
      const res = await apiFetch<readonly Campaign[]>(`/api/campaigns${query}`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load campaigns')
      return res.data
    },
    staleTime: 30_000,
  })
}

export function useCampaign(id: string) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: campaignKeys.detail(id),
    queryFn: async (): Promise<Campaign> => {
      const res = await apiFetch<Campaign>(`/api/campaigns/${id}`)
      if (!res.success || !res.data) throw new Error(res.error ?? `Campaign ${id} not found`)
      return res.data
    },
    enabled: Boolean(id),
  })
}

export function useCampaignStats(id: string) {
  const apiFetch = useApi()

  return useQuery<CampaignStats>({
    queryKey: campaignKeys.stats(id),
    queryFn: async (): Promise<CampaignStats> => {
      const res = await apiFetch<CampaignStats>(`/api/campaigns/${id}/stats`)
      if (!res.success || !res.data)
        throw new Error(res.error ?? `Stats for campaign ${id} not found`)
      return res.data
    },
    enabled: Boolean(id),
    staleTime: 60_000,
  })
}

export type CampaignLeadsResult = {
  readonly leads: readonly Lead[]
  /** True campaign size from the API count — NOT the fetched page length. */
  readonly total: number
}

export function useCampaignLeads(campaignId: string) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: [...campaignKeys.detail(campaignId), 'leads'] as const,
    queryFn: async (): Promise<CampaignLeadsResult> => {
      // limit=500 (server max) so the table holds the whole campaign in one
      // request. Without it the server defaults to 50 and the page rendered
      // "Leads (50)" for a 291-lead campaign. `total` is the real count even
      // if a campaign ever exceeds 500 rows.
      const res = await apiFetch<{ data: readonly Lead[]; total: number }>(
        `/api/campaigns/${campaignId}/leads?limit=500`,
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load campaign leads')
      return { leads: res.data.data, total: res.data.total }
    },
    enabled: Boolean(campaignId),
    staleTime: 30_000,
  })
}

export function useCreateCampaign() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (input: CreateCampaignInput): Promise<Campaign> => {
      const res = await apiFetch<Campaign>('/api/campaigns', {
        method: 'POST',
        body: input,
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to create campaign')
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() })
    },
  })
}

/**
 * Fields accepted by PUT /api/campaigns/:id. `settings` is a partial patch:
 * the backend merges the keys into the stored campaign settings JSON rather
 * than replacing the whole blob.
 */
export type UpdateCampaignPatch = Partial<Campaign> & {
  readonly settings?: Record<string, unknown>
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string
      updates: UpdateCampaignPatch
    }): Promise<Campaign> => {
      const res = await apiFetch<Campaign>(`/api/campaigns/${id}`, {
        method: 'PUT',
        body: updates,
      })
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to update campaign ${id}`)
      return res.data
    },
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: campaignKeys.detail(id) })
    },
  })
}

export function useActivateCampaign() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (id: string): Promise<Campaign> => {
      const res = await apiFetch<Campaign>(`/api/campaigns/${id}/activate`, {
        method: 'POST',
      })
      if (!res.success || !res.data)
        throw new Error(res.error ?? `Failed to activate campaign ${id}`)
      return res.data
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: campaignKeys.detail(id) })
    },
  })
}

export function usePauseCampaign() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (id: string): Promise<Campaign> => {
      const res = await apiFetch<Campaign>(`/api/campaigns/${id}/pause`, {
        method: 'POST',
      })
      if (!res.success || !res.data)
        throw new Error(res.error ?? `Failed to pause campaign ${id}`)
      return res.data
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: campaignKeys.detail(id) })
    },
  })
}

export function useToggleTestMode() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }): Promise<{ test_mode: boolean; test_mode_expires_at: string | null }> => {
      const res = await apiFetch<{ test_mode: boolean; test_mode_expires_at: string | null }>(`/api/campaigns/${id}/test-mode`, {
        method: 'POST',
        body: { enabled },
      })
      if (!res.success || !res.data)
        throw new Error(res.error ?? 'Failed to toggle test mode')
      return res.data
    },
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() })
    },
  })
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await apiFetch(`/api/campaigns/${id}`, { method: 'DELETE' })
      if (!res.success) throw new Error(res.error ?? `Failed to delete campaign ${id}`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() })
    },
  })
}

export function useAddLeadsToCampaign() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ campaignId, leadIds }: { campaignId: string; leadIds: readonly string[] }): Promise<{ added: number }> => {
      const res = await apiFetch<{ added: number }>(`/api/campaigns/${campaignId}/leads`, {
        method: 'POST',
        body: { lead_ids: leadIds },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to add leads')
      return res.data
    },
    onSuccess: (_data, { campaignId }) => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaignId) })
      void queryClient.invalidateQueries({ queryKey: [...campaignKeys.detail(campaignId), 'leads'] })
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() })
    },
  })
}

export function useRemoveLeadFromCampaign() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ campaignId, leadId }: { campaignId: string; leadId: string }): Promise<void> => {
      const res = await apiFetch(`/api/campaigns/${campaignId}/leads/${leadId}`, { method: 'DELETE' })
      if (!res.success) throw new Error(res.error ?? 'Failed to remove lead')
    },
    onSuccess: (_data, { campaignId }) => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaignId) })
      void queryClient.invalidateQueries({ queryKey: [...campaignKeys.detail(campaignId), 'leads'] })
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() })
    },
  })
}

export function useUpdateSequence() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ campaignId, sequence }: { campaignId: string; sequence: unknown }): Promise<void> => {
      const res = await apiFetch(`/api/campaigns/${campaignId}/sequence`, {
        method: 'PUT',
        body: sequence,
      })
      if (!res.success) throw new Error(res.error ?? 'Failed to update sequence')
    },
    onSuccess: (_data, { campaignId }) => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaignId) })
    },
  })
}

// ---------------------------------------------------------------------------
// Messaging brief (meeting 2026-09-18): GET/PUT /api/campaigns/:id/brief
// ---------------------------------------------------------------------------

export type CampaignBriefVersion = {
  readonly version: number
  readonly rendered: string
  readonly created_at: string
}

export type CampaignBriefResponse = {
  readonly brief: CampaignBrief | null
  readonly rendered: string
  readonly required: boolean
  readonly confirmed_at: string | null
  readonly version: number
  readonly versions: readonly CampaignBriefVersion[]
}

export function useCampaignBrief(id: string, enabled = true) {
  const apiFetch = useApi()
  return useQuery({
    queryKey: [...campaignKeys.detail(id), 'brief'] as const,
    queryFn: async () => {
      const res = await apiFetch<CampaignBriefResponse>(`/api/campaigns/${id}/brief`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load the messaging brief')
      return res.data
    },
    enabled: Boolean(id) && enabled,
  })
}

export function useSaveCampaignBrief() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  return useMutation({
    mutationFn: async ({ id, brief }: { id: string; brief: CampaignBrief }) => {
      const res = await apiFetch<{ version: number; confirmed_at: string }>(`/api/campaigns/${id}/brief`, {
        method: 'PUT',
        body: { brief },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to save the messaging brief')
      return res.data
    },
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() })
    },
  })
}
